import { format } from 'date-fns';

import { cn } from '../../lib/cn';
import type { CustomerOption, LeadOption, QuoteRow } from './quote.schema';
import { QUOTE_STATUS_LABELS_HE, formatILS, statusBadgeClass } from './quote.schema';
import styles from './quotes.module.css';

interface Props {
  quotes: QuoteRow[];
  customers: CustomerOption[];
  leads: LeadOption[];
  onSelect: (quote: QuoteRow) => void;
}

function StatusBadge({ status }: { status: string }) {
  const safe = (QUOTE_STATUS_LABELS_HE as Record<string, string>)[status] ?? status;
  return <span className={cn(styles.badge, styles[statusBadgeClass(status)])}>{safe}</span>;
}

export function QuotesList({ quotes, customers, leads, onSelect }: Props) {
  const customerById = customers.reduce<Record<string, string>>((acc, c) => {
    acc[c.id] = c.name;
    return acc;
  }, {});
  const leadById = leads.reduce<Record<string, string>>((acc, l) => {
    acc[l.id] = l.full_name;
    return acc;
  }, {});

  return (
    <div className={styles.list}>
      <div className={styles.tableHead}>
        <span>מספר</span>
        <span>לקוח / ליד</span>
        <span>סטטוס</span>
        <span>סכום</span>
        <span>תוקף עד</span>
        <span>נוצרה</span>
      </div>
      {quotes.map((q) => {
        const customerName = q.customer_id ? customerById[q.customer_id] : null;
        const leadName = q.source_lead_id ? leadById[q.source_lead_id] : null;
        const display = customerName ?? leadName ?? '—';
        return (
          <div
            key={q.id}
            className={styles.tableRow}
            role="button"
            tabIndex={0}
            onClick={() => { onSelect(q); }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onSelect(q);
              }
            }}
          >
            <span className={styles.quoteNumber}>
              <bdi dir="ltr">{q.quote_number}</bdi>
            </span>
            <span>{display}</span>
            <span><StatusBadge status={q.status} /></span>
            <span className={styles.subtotal}><bdi>{formatILS(q.value_amount)}</bdi></span>
            <span className={styles.rowMeta}>
              {q.valid_until ? format(new Date(q.valid_until), 'dd/MM/yyyy') : '—'}
            </span>
            <span className={styles.rowMeta}>{format(new Date(q.created_at), 'dd/MM/yyyy')}</span>
          </div>
        );
      })}
    </div>
  );
}
